import { plural, t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { TireQuoteRecordCustomer } from '@navio/nsdk';
import { NavioOverlayActions, NoddiButton, NoddiDialog } from 'noddi-ui-web';
import { customerRoutes, formatLicensePlate } from 'noddi-util';
import { useNavigate } from 'react-router-dom';

export type AcceptResult =
  | { kind: 'on_delivery_ok'; offer: TireQuoteRecordCustomer }
  | { kind: 'pay_before_ok'; offer: TireQuoteRecordCustomer; sessionId: string }
  | { kind: 'pay_before_ok_no_session'; offer: TireQuoteRecordCustomer }
  | { kind: 'failed'; offer: TireQuoteRecordCustomer; message: string };

type Props = {
  results: AcceptResult[];
  onClose: () => void;
};

export const BatchResultModal = ({ results, onClose }: Props) => {
  const navigate = useNavigate();
  const failed = results.filter(
    (result): result is Extract<AcceptResult, { kind: 'failed' }> => result.kind === 'failed'
  );
  const payBefore = results.filter(
    (result): result is Extract<AcceptResult, { kind: 'pay_before_ok' }> => result.kind === 'pay_before_ok'
  );
  const onDelivery = results.filter((result) => result.kind === 'on_delivery_ok');
  const payBeforeNoSession = results.filter((result) => result.kind === 'pay_before_ok_no_session');
  const payBeforeCount = payBefore.length;
  const failedCount = failed.length;

  return (
    <NoddiDialog
      title={t`Batch accept results`}
      open
      onClose={onClose}
      maxWidth='md'
      footer={
        <NavioOverlayActions>
          <NoddiButton onClick={onClose}>
            <Trans>Close</Trans>
          </NoddiButton>
        </NavioOverlayActions>
      }
    >
      <div className='flex flex-col gap-3 px-1 pb-2 text-sm'>
        {onDelivery.length > 0 && (
          <div className='rounded-lg bg-success20 p-3 text-successDark'>
            {plural(onDelivery.length, {
              one: '# offer accepted · invoiced',
              other: '# offers accepted · invoiced'
            })}
          </div>
        )}

        {payBefore.length > 0 && (
          <div className='flex flex-col gap-2'>
            <div className='text-sm font-semibold text-primary'>
              <Trans>Awaiting payment ({payBeforeCount})</Trans>
            </div>
            {payBefore.map((result) => (
              <div
                key={result.offer.id}
                className='flex items-center justify-between gap-3 rounded-lg border border-border p-3'
              >
                <div>
                  <div className='font-semibold'>
                    {result.offer.car.make} {result.offer.car.model}
                  </div>
                  <div className='text-xs text-muted-foreground'>
                    {formatLicensePlate(result.offer.car.licensePlateNumber)}
                  </div>
                </div>
                <NoddiButton
                  size='small'
                  // oxlint-disable-next-line typescript/no-misused-promises -- async onClick runs navigate(); React discards the returned promise, so a rejection is unhandled unless the body catches
                  onClick={() =>
                    navigate(customerRoutes.tireOfferConfirmation.getPath({ sessionId: result.sessionId }))
                  }
                >
                  <Trans>Pay now</Trans>
                </NoddiButton>
              </div>
            ))}
          </div>
        )}

        {payBeforeNoSession.length > 0 && (
          <div className='rounded-lg bg-warning50 p-3 text-warningDark'>
            {plural(payBeforeNoSession.length, {
              one: "# offer accepted, but the payment session couldn't be started. Please open the offer to retry.",
              other: "# offers accepted, but the payment session couldn't be started. Please open the offer to retry."
            })}
          </div>
        )}

        {failed.length > 0 && (
          <div className='flex flex-col gap-2'>
            <div className='text-sm font-semibold text-dangerDark'>
              <Trans>Failed ({failedCount})</Trans>
            </div>
            {failed.map((result, index) => (
              <div
                key={`${result.offer.id}-${index}`}
                className='rounded-lg border border-danger50 bg-danger50/40 p-3 text-xs text-dangerDark'
              >
                <div className='font-semibold'>
                  {result.offer.car.make} {result.offer.car.model}{' '}
                  <span className='font-mono text-[11px]'>
                    {formatLicensePlate(result.offer.car.licensePlateNumber)}
                  </span>
                </div>
                <div>{result.message}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </NoddiDialog>
  );
};
